import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { parsePhoneNumber } from 'libphonenumber-js'
import type { ChangeEvent } from 'react'
import { Form, data, redirect, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import {
	AuthAlt,
	AuthConsent,
	AuthErrors,
	AuthField,
	AuthFieldRow,
	AuthHeading,
	AuthPhoneField,
	AuthShell,
	AuthStrong,
	AuthSubmit,
	SocialButtons,
} from '#app/components/site/auth'
import { getLandlordSelfSignupMode } from '#app/services/module-access.server'
import { getAuthConfig } from '#app/utils/auth-config.server'
import { getPasswordHash, requireAnonymous } from '#app/utils/auth.server'
import { capitalizeWords } from '#app/utils/capitalize'
import { db } from '#app/utils/db.server'
import { requireNotSpam } from '#app/utils/honeypot.server'
import { useIsPending } from '#app/utils/misc'
import {
	LANDLORD_SIGNUP_CLOSED_TOAST,
	resolveLandlordSelfSignup,
} from '#app/utils/module-access/landlord-self-signup'
import { withSearchParams } from '#app/utils/site/auth-ui'
import { siteContent } from '#app/utils/site/content'
import { siteLinks } from '#app/utils/site/site-links'
import { redirectWithToast } from '#app/utils/toast.server'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation'
import { verifySessionStorage } from '#app/utils/verification.server'
import { teamInvitations } from '#db/schema/team-invitations'
import type { Route } from './+types/landlord.signup'
import {
	SIGNUP_KEYS,
	readSignupResume,
	startSignupCode,
} from './verify-account.server'

type SEOHandle = { getSitemapEntries: () => null }

export const handle = {
	getSitemapEntries: () => null,
} satisfies SEOHandle

export const links = siteLinks()

const PhoneSchema = z
	.string({ required_error: 'Phone number is required' })
	.min(1, { message: 'Phone number is required' })
	.refine(
		(phone) => {
			if (!phone || phone.trim() === '') {
				return false
			}
			try {
				const parsed = parsePhoneNumber(phone)
				return parsed?.isValid() ?? false
			} catch {
				try {
					const parsed = parsePhoneNumber(phone, 'CA')
					return parsed?.isValid() ?? false
				} catch {
					return false
				}
			}
		},
		{
			message: 'Please enter a valid phone number',
		},
	)

const FirstNameSchema = z
	.string({ required_error: 'First name is required' })
	.min(1, { message: 'First name is required' })
	.max(50, { message: 'First name is too long' })
	// Title-case so the stored name has a capital first letter regardless of typing.
	.transform((value) => capitalizeWords(value))

const LastNameSchema = z
	.string({ required_error: 'Last name is required' })
	.min(1, { message: 'Last name is required' })
	.max(50, { message: 'Last name is too long' })
	.transform((value) => capitalizeWords(value))

const CompanyNameSchema = z
	.string({ required_error: 'Company name is required' })
	.min(2, { message: 'Company name must be at least 2 characters' })
	.max(100, { message: 'Company name is too long' })

const SignupSchema = z
	.object({
		firstname: FirstNameSchema,
		lastname: LastNameSchema,
		companyName: CompanyNameSchema,
		email: EmailSchema,
		phone: PhoneSchema,
		password: PasswordSchema,
		confirmPassword: PasswordSchema,
	})
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	const authConfig = getAuthConfig('landlord')
	const url = new URL(request.url)
	const inviteTokenParam = url.searchParams.get('invite_token')

	// TT-197: public sign-up is paused unless Superadmin ▸ Module Access opens
	// it. An invitation link still reaches the form (the token is validated
	// below); a bad token falls through to the invitation checks.
	if (
		!resolveLandlordSelfSignup({
			accessMode: await getLandlordSelfSignupMode(),
			inviteToken: inviteTokenParam,
		})
	) {
		throw await redirectWithToast(
			'/landlord/login',
			LANDLORD_SIGNUP_CLOSED_TOAST,
		)
	}

	// Resolve the canonical invite email server-side from the token.
	// Never trust an `email` query param: the token is the only thing tying
	// this signup to a specific invitation and its tenant/role.
	let inviteToken: string | null = null
	let inviteEmail: string | null = null
	if (inviteTokenParam) {
		const invitation = await db.query.teamInvitations.findFirst({
			where: (t, { eq }) => eq(t.token, inviteTokenParam),
			columns: { email: true, status: true, expiresAt: true },
		})

		if (!invitation) {
			throw await redirectWithToast('/landlord/login', {
				title: 'Invalid Invitation',
				description:
					'This invitation link is invalid or has already been used.',
				type: 'error',
			})
		}
		if (invitation.status !== 'pending') {
			throw await redirectWithToast('/landlord/login', {
				title: 'Invitation Unavailable',
				description: `This invitation has been ${invitation.status}.`,
				type: 'error',
			})
		}
		if (invitation.expiresAt && new Date(invitation.expiresAt) < new Date()) {
			throw await redirectWithToast('/landlord/login', {
				title: 'Invitation Expired',
				description:
					'This invitation has expired. Please ask your admin to resend it.',
				type: 'error',
			})
		}

		inviteToken = inviteTokenParam
		inviteEmail = invitation.email
	}

	// "Back To Sign Up" from the code screen: fill the form again.
	const resume =
		url.searchParams.get('resume') === '1'
			? await readSignupResume(request)
			: null
	return { authConfig, inviteToken, inviteEmail, resume }
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await requireNotSpam(formData)

	const inviteTokenFromForm = formData.get('inviteToken')?.toString() || null

	// TT-197: re-check at submit (an old tab, a hand-built POST). The invite
	// token is re-validated against the DB in the schema below.
	if (
		!resolveLandlordSelfSignup({
			accessMode: await getLandlordSelfSignupMode(),
			inviteToken: inviteTokenFromForm,
		})
	) {
		throw await redirectWithToast(
			'/landlord/login',
			LANDLORD_SIGNUP_CLOSED_TOAST,
		)
	}

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const submittedEmail = data.email.toLowerCase()

			// If signing up through a team invitation, the email is bound to the
			// invitation. The client renders the field read-only, but the form
			// can still be tampered with — re-validate here against the DB.
			if (inviteTokenFromForm) {
				const invitation = await db.query.teamInvitations.findFirst({
					where: (t, { eq }) => eq(t.token, inviteTokenFromForm),
					columns: { email: true, status: true, expiresAt: true },
				})

				if (
					!invitation ||
					invitation.status !== 'pending' ||
					(invitation.expiresAt && new Date(invitation.expiresAt) < new Date())
				) {
					ctx.addIssue({
						path: ['email'],
						code: z.ZodIssueCode.custom,
						message: 'This invitation is no longer valid.',
					})
					return
				}

				if (submittedEmail !== invitation.email.toLowerCase()) {
					ctx.addIssue({
						path: ['email'],
						code: z.ZodIssueCode.custom,
						message: 'This invitation was sent to a different email address.',
					})
					return
				}
			}

			const existingUser = await db.query.user.findFirst({
				columns: { id: true },
				where: (user, { eq }) => eq(user.email, submittedEmail),
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this email',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{
				status: 500,
			},
		)
	}
	const { email, firstname, lastname, companyName, phone, password } =
		submission.value
	// What the form had waits in the verify session until the code is right.
	const session = await verifySessionStorage.getSession()
	// Only the hash waits in the cookie, never the password itself.
	session.set(SIGNUP_KEYS.passwordHash, await getPasswordHash(password))
	session.set(SIGNUP_KEYS.firstName, firstname)
	session.set(SIGNUP_KEYS.lastName, lastname)
	session.set(SIGNUP_KEYS.phone, phone)
	session.set(SIGNUP_KEYS.companyName, companyName)
	session.set(SIGNUP_KEYS.userType, 'landlord')
	const redirectTo = formData.get('redirectTo')?.toString()
	if (redirectTo) session.set(SIGNUP_KEYS.redirectTo, redirectTo)
	if (inviteTokenFromForm) {
		session.set(SIGNUP_KEYS.inviteToken, inviteTokenFromForm)
	}
	return startSignupCode({ request, session, email })
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Sign Up | TrueTenant - Landlord' }]
}

export default function LandlordSignupRoute({
	actionData,
	loaderData,
}: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const authConfig = loaderData.authConfig
	const inviteToken = loaderData.inviteToken
	const inviteEmail = loaderData.inviteEmail

	// Build login link preserving redirectTo param
	const loginLink = withSearchParams(authConfig.loginPath, { redirectTo })

	const [form, fields] = useForm<z.input<typeof SignupSchema>>({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		defaultValue: loaderData.resume ?? undefined,
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	// Title-case as the user types (caret preserved: the length is unchanged),
	// matching the screening name fields.
	const titleCaseAsTyped = (event: ChangeEvent<HTMLInputElement>) => {
		const el = event.currentTarget
		const pos = el.selectionStart
		el.value = capitalizeWords(el.value)
		if (pos !== null) el.setSelectionRange(pos, pos)
	}

	const copy = siteContent.auth
	const labels = copy.fields

	return (
		<AuthShell audience="landlord">
			<Form method="POST" {...getFormProps(form)}>
				<HoneypotInputs />
				{redirectTo && (
					<input type="hidden" name="redirectTo" value={redirectTo} />
				)}
				{inviteToken && (
					<input type="hidden" name="inviteToken" value={inviteToken} />
				)}
				<AuthHeading eyebrow={copy.signup.eyebrow} title={copy.role.landlord} />
				<AuthFieldRow>
					<AuthField
						label={labels.firstName}
						hint={siteContent.auth.hints.firstName}
						autoComplete="given-name"
						inputProps={{
							...getInputProps(fields.firstname, { type: 'text' }),
							onChange: titleCaseAsTyped,
						}}
						errors={fields.firstname.errors}
					/>
					<AuthField
						label={labels.lastName}
						hint={siteContent.auth.hints.lastName}
						autoComplete="family-name"
						inputProps={{
							...getInputProps(fields.lastname, { type: 'text' }),
							onChange: titleCaseAsTyped,
						}}
						errors={fields.lastname.errors}
					/>
				</AuthFieldRow>
				<AuthField
					label={labels.company}
					hint={siteContent.auth.hints.company}
					autoComplete="organization"
					inputProps={getInputProps(fields.companyName, { type: 'text' })}
					errors={fields.companyName.errors}
				/>
				<AuthField
					label={labels.email}
					hint={siteContent.auth.hints.email}
					type="email"
					autoComplete="email"
					inputProps={{
						...getInputProps(fields.email, { type: 'email' }),
						// A team invitation binds the email: the field is locked to the
						// invited address (the action re-checks it against the DB).
						defaultValue: inviteEmail || undefined,
						readOnly: Boolean(inviteEmail),
						'aria-readonly': inviteEmail ? true : undefined,
						title: inviteEmail
							? 'This email is locked to your invitation.'
							: undefined,
					}}
					errors={fields.email.errors}
				/>
				{inviteEmail ? (
					<p className="-mt-1 mb-2.5 pl-[18px] text-[13px] leading-[1.45] text-tt-muted min-[601px]:mb-[clamp(8px,1.5vh,14px)] min-[601px]:pl-[22px]">
						This invitation is locked to <AuthStrong>{inviteEmail}</AuthStrong>.
						To accept it, sign up with this email.
					</p>
				) : null}
				<AuthPhoneField
					label={labels.phone}
					inputProps={getInputProps(fields.phone, { type: 'text' })}
					errors={fields.phone.errors}
				/>
				<AuthField
					label={labels.password}
					hint={siteContent.auth.hints.password}
					type="password"
					autoComplete="new-password"
					inputProps={getInputProps(fields.password, { type: 'password' })}
					errors={fields.password.errors}
				/>
				<AuthField
					label={labels.confirmPassword}
					hint={siteContent.auth.hints.confirmPassword}
					type="password"
					autoComplete="new-password"
					inputProps={getInputProps(fields.confirmPassword, {
						type: 'password',
					})}
					errors={fields.confirmPassword.errors}
				/>
				<AuthErrors errors={form.errors} id={form.errorId} />
				<AuthSubmit pending={isPending}>{copy.signup.submit}</AuthSubmit>
			</Form>
			<SocialButtons
				type="Continue"
				redirectTo={redirectTo}
				userType="landlord"
				teamInvite={inviteToken}
			/>
			<AuthAlt
				text={copy.signup.alt}
				link={{ label: copy.signup.altLink, to: loginLink }}
			/>
			<AuthConsent kind="signup" />
		</AuthShell>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
